import React from "react";
import {
  Box,
  Button,
  Chip,
  GlobalStyles,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import {
  cleanFieldCommand,
  findFieldByVoice,
  isAffirmative,
  isNegative,
  matchVoiceOption,
  normalizeVoiceText,
  parseVoiceDateTime,
  parseVoiceNumber,
  parseVoicePhone,
} from "./ownerVoiceParsers";
import { adaptiveDecision, ownerRouteContext, rankAdaptiveActions } from "./ownerVoiceNavigationLearning";

const OwnerVoiceContext = React.createContext(null);

const COMMANDS = {
  next: ["siguiente", "seguir", "continua", "continuar", "omitir", "saltear", "pasar"],
  previous: ["anterior", "volver al campo anterior", "campo anterior", "atras"],
  missing: ["que falta", "que me falta", "campos pendientes", "faltantes"],
  save: ["guardar", "guarda", "confirmar", "confirmo", "terminar", "finalizar", "listo"],
  cancel: ["cancelar", "cancela", "cerrar formulario", "salir del formulario"],
  stop: ["parar voz", "detener voz", "silencio", "deja de escuchar", "terminar conversacion"],
  powerOff: ["apagar", "apagate", "apagar jarvis", "dormite", "dormi jarvis", "desactivar jarvis"],
  powerOn: ["encender", "encendete", "encender jarvis", "despertar jarvis", "despierta jarvis", "activar jarvis"],
  startForm: ["completar formulario", "completar por voz", "rellenar formulario", "llenar formulario", "ayudame con este formulario"],
  options: ["que puedo hacer", "que se puede hacer", "opciones", "acciones", "ayuda de voz", "comandos"],
  repeat: ["repeti", "repetir", "repite", "otra vez", "decilo de nuevo", "decimelo de nuevo", "no entendi"],
};

function matchesCommand(text, variants = []) {
  const normalized = normalizeVoiceText(text);
  return variants.some((item) => normalized === item || normalized.startsWith(`${item} `));
}

function hasValue(value) {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function visibleFields(config) {
  const values = config?.values || {};
  return (config?.fields || []).filter((field) => !field.when || field.when(values));
}

function requiredMissing(config) {
  const values = config?.values || {};
  return visibleFields(config).filter((field) => {
    const required = typeof field.required === "function" ? field.required(values) : field.required;
    return required && !hasValue(values[field.name]);
  });
}

function formatOptions(field) {
  if (field.speakOptions === false || !field.options?.length) return "";
  const labels = field.options.map((row) => row.label).filter(Boolean);
  if (!labels.length) return "";
  if (labels.length <= 4) return ` Opciones: ${labels.join(", ")}.`;
  return " Elegí una de las opciones visibles.";
}

function parseFieldValue(field, raw, config) {
  const clean = cleanFieldCommand(raw, field);
  if (field.parse) return field.parse(clean, config?.values || {});
  if (field.kind === "number") return parseVoiceNumber(clean);
  if (field.kind === "choice") return matchVoiceOption(clean, field.options || [])?.value ?? null;
  if (field.kind === "datetime") return parseVoiceDateTime(clean);
  if (field.kind === "phone") return parseVoicePhone(clean);
  if (field.kind === "email") return String(clean).replace(/\s+arroba\s+/i, "@").replace(/\s+punto\s+/i, ".").replace(/\s/g, "");
  return String(clean).trim();
}

function fieldValueLabel(field, value) {
  if (!hasValue(value)) return "vacío";
  if (field.kind === "choice") return field.options?.find((row) => String(row.value) === String(value))?.label || String(value);
  return String(value);
}


function looksLikePromptEcho(transcript, prompt) {
  const heard = normalizeVoiceText(transcript).split(" ").filter((word) => word.length > 1);
  const spoken = normalizeVoiceText(prompt).split(" ").filter((word) => word.length > 1);
  if (!heard.length || !spoken.length) return false;
  // Respuestas cortas como "pago mensual", "sí" o "gasto" son justo
  // las que queremos aceptar encima de la locución. Sólo tratamos como eco
  // una frase suficientemente larga y muy parecida a lo que el bot está leyendo.
  if (heard.length < 4) return false;
  const spokenSet = new Set(spoken);
  const overlap = heard.filter((word) => spokenSet.has(word)).length / heard.length;
  return overlap >= 0.86;
}


function stopMediaStream(stream) {
  try {
    stream?.getTracks?.().forEach((track) => {
      try { track.stop?.(); } catch { /* noop */ }
    });
  } catch { /* noop */ }
}

function microphoneErrorMessage(error) {
  const name = String(error?.name || "");
  if (name === "NotAllowedError" || name === "SecurityError") return "El navegador no me dejó abrir el audio real. Revisá el permiso del sitio y el permiso del sistema operativo.";
  if (name === "NotFoundError" || name === "DevicesNotFoundError") return "No encontré ningún micrófono disponible en este equipo.";
  if (name === "NotReadableError" || name === "TrackStartError") return "El micrófono existe pero no se puede abrir. Puede estar siendo usado en exclusiva por otra aplicación.";
  if (name === "OverconstrainedError") return "El dispositivo de audio no cumple las restricciones solicitadas. Probá otro micrófono.";
  if (name === "AbortError") return "El navegador abortó la apertura del micrófono. Probá otra vez.";
  return `No pude abrir el audio real${name ? ` (${name})` : ""}.`;
}

function recognitionErrorMessage(code, microphoneVerified = false) {
  const error = String(code || "");
  if (error === "audio-capture") return "SpeechRecognition no recibe audio del micrófono. Voy a volver a validar el dispositivo.";
  if (error === "network") return microphoneVerified
    ? "El micrófono funciona, pero el servicio de dictado del navegador no está respondiendo. Revisá conexión/bloqueadores o probá Chrome/Edge."
    : "El servicio de dictado del navegador no está respondiendo.";
  if (error === "service-not-allowed") return "El micrófono está disponible, pero este navegador bloquea el servicio de reconocimiento de voz. Probá Chrome o Edge sin bloqueo del servicio de voz.";
  if (error === "language-not-supported") return "El reconocimiento de voz no admite es-AR en este navegador.";
  if (error === "not-allowed") return microphoneVerified
    ? "El audio real funciona, pero el navegador bloqueó SpeechRecognition. Revisá la configuración de reconocimiento de voz del navegador."
    : "El navegador bloqueó el acceso de voz. Revisá el permiso de micrófono y probá nuevamente.";
  return `El dictado falló${error ? ` (${error})` : ""}. Probá otra vez.`;
}

function contextLabel(context) {
  const entity = context?.entity || context;
  return entity?.name || entity?.title || entity?.company || "esto";
}

function looksLikeGlobalEscapeIntent(value = "") {
  const text = normalizeVoiceText(value);
  const navigation = /\b(?:abr\w*|mostr\w*|mir\w*|llev\w*|ver|ir)\b/.test(text)
    && /\b(?:ficha|proyecto|proyectos|cliente|clientes|persona|rutina|rutinas|dia|fecha|movimientos|finanzas|calendario|agenda)\b/.test(text);
  const projectNote = /\b(?:anot\w*|apunt\w*|agreg\w*|carg\w*|registr\w*)\b/.test(text)
    && /\b(?:proyecto|frente|iniciativa)\b/.test(text);
  const financeView = /\b(?:mostr\w*|mir\w*|ver|list\w*)\b/.test(text)
    && /\b(?:entrada|entradas|ingreso|ingresos|pago|pagos|cobro|cobros|movimiento|movimientos)\b/.test(text);
  return navigation || projectNote || financeView;
}

function isOwnerHomeResetIntent(value = "") {
  const text = normalizeVoiceText(value).trim();
  return /^(?:inicio|menu principal|panel de hoy|volver al inicio|volver a inicio|ir al inicio|anda al inicio|vamos al inicio|navegar al inicio|navegar a inicio|volver a hoy|ir a hoy|navegar a hoy)$/.test(text);
}

function focusVoiceField(name) {
  if (typeof document === "undefined" || !name) return;
  document.querySelectorAll('[data-owner-voice-active="true"]').forEach((node) => node.removeAttribute("data-owner-voice-active"));
  const escaped = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(name) : name;
  const input = document.querySelector(`[data-owner-voice-field="${escaped}"]`);
  if (!input) return;
  const wrapper = input.closest(".MuiFormControl-root") || input.closest(".MuiTextField-root") || input.parentElement;
  wrapper?.setAttribute("data-owner-voice-active", "true");
  wrapper?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  const focusTarget = input.matches?.('[role="combobox"]') ? input : (wrapper?.querySelector?.('[role="combobox"]') || input);
  window.setTimeout(() => focusTarget?.focus?.(), 80);
}

function highlightOwnerSection(sectionId, { scroll = true, duration = 4200 } = {}) {
  if (typeof document === "undefined" || !sectionId) return false;
  document.querySelectorAll('[data-owner-jarvis-focus="true"]').forEach((node) => node.removeAttribute("data-owner-jarvis-focus"));
  const escaped = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(sectionId) : sectionId;
  const node = document.querySelector(`[data-owner-jarvis-section="${escaped}"]`);
  if (!node) return false;
  node.setAttribute("data-owner-jarvis-focus", "true");
  if (scroll) node.scrollIntoView?.({ behavior: "smooth", block: "center", inline: "nearest" });
  window.setTimeout(() => {
    if (node.getAttribute("data-owner-jarvis-focus") === "true") node.removeAttribute("data-owner-jarvis-focus");
  }, Math.max(1200, Number(duration || 0)));
  return true;
}

export function OwnerVoiceOperatorProvider(props) {
  const parentVoice = React.useContext(OwnerVoiceContext);
  if (parentVoice) return props.children;
  return <OwnerVoiceOperatorProviderInner {...props} />;
}

function OwnerVoiceOperatorProviderInner({ actions = {}, introMessage = "", onUnhandledUtterance = null, hasPriorityConversation = null, resetConversationState = null, alwaysOn = false, loadNavigationLearning = null, recordNavigationLearning = null, routeContext = "", children }) {
  const [activeFormId, setActiveFormId] = React.useState("");
  const [currentFieldName, setCurrentFieldName] = React.useState("");
  const [status, setStatus] = React.useState("idle");
  const [isListening, setIsListening] = React.useState(false);
  const [isSpeaking, setIsSpeaking] = React.useState(false);
  const [interimHeard, setInterimHeard] = React.useState("");
  const [message, setMessage] = React.useState("¿Qué querés hacer?");
  const [lastHeard, setLastHeard] = React.useState("");
  const [confirmation, setConfirmation] = React.useState(false);
  const [handsFree, setHandsFreeState] = React.useState(false);
  const [alwaysOnEnabled, setAlwaysOnEnabledState] = React.useState(() => {
    if (!alwaysOn) return false;
    try { return window.localStorage.getItem("wp-owner-jarvis-always-on") !== "0"; } catch { return true; }
  });
  const [needsMicPermission, setNeedsMicPermission] = React.useState(false);
  const [microphoneState, setMicrophoneState] = React.useState("unknown");
  const [microphoneError, setMicrophoneError] = React.useState("");
  const [autoRead, setAutoRead] = React.useState(() => {
    try { return window.localStorage.getItem("wp-owner-voice-auto-read") !== "0"; } catch { return true; }
  });
  const [speechRate, setSpeechRate] = React.useState(() => {
    try {
      const stored = Number(window.localStorage.getItem("wp-owner-voice-rate"));
      return [1, 1.15, 1.3, 1.5, 1.7].includes(stored) ? stored : 1.5;
    } catch { return 1.5; }
  });

  const [navigationLearning, setNavigationLearning] = React.useState([]);

  const formGetterRef = React.useRef(null);
  const activeFormIdRef = React.useRef("");
  const currentFieldRef = React.useRef("");
  const recognitionRef = React.useRef(null);
  const microphoneVerifiedRef = React.useRef(false);
  const microphoneCheckPromiseRef = React.useRef(null);
  const handsFreeRef = React.useRef(false);
  const alwaysOnEnabledRef = React.useRef(alwaysOnEnabled);
  const pendingFormRef = React.useRef("");
  const processRef = React.useRef(null);
  const mountedRef = React.useRef(true);
  const speechTokenRef = React.useRef(0);
  const recognitionTokenRef = React.useRef(0);
  const pendingListenTimerRef = React.useRef(null);
  const pendingTranscriptRef = React.useRef("");
  const lastQueuedVoiceFinalRef = React.useRef({ text: "", at: 0 });
  const recognitionModeRef = React.useRef("handsfree");
  const intentionalRecognitionStopRef = React.useRef(false);
  const currentSpeechTextRef = React.useRef("");
  const speechStartedAtRef = React.useRef(0);
  const isSpeakingRef = React.useRef(false);
  const continuationRef = React.useRef(null);
  const actionContextRef = React.useRef(null);
  const queuedActionAfterSaveRef = React.useRef("");
  const utteranceQueueRef = React.useRef(Promise.resolve());
  const pendingNavigationChoiceRef = React.useRef(null);
  const lastAdaptiveExecutionRef = React.useRef(null);
  const conversationHandlerRef = React.useRef(null);
  const previousRouteContextRef = React.useRef(routeContext);

  const speechSupported = typeof window !== "undefined" && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
  const speakSupported = typeof window !== "undefined" && Boolean(window.speechSynthesis && window.SpeechSynthesisUtterance);

  React.useEffect(() => { isSpeakingRef.current = isSpeaking; }, [isSpeaking]);
  React.useEffect(() => { alwaysOnEnabledRef.current = alwaysOnEnabled; }, [alwaysOnEnabled]);

  React.useEffect(() => {
    let active = true;
    if (typeof loadNavigationLearning !== "function") return undefined;
    loadNavigationLearning()
      .then((response) => { if (active) setNavigationLearning(response?.learning || []); })
      .catch(() => undefined);
    return () => { active = false; };
  }, [loadNavigationLearning]);

  React.useEffect(() => () => {
    mountedRef.current = false;
    speechTokenRef.current += 1;
    recognitionTokenRef.current += 1;
    if (pendingListenTimerRef.current) window.clearTimeout(pendingListenTimerRef.current);
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (recognition) {
      recognition.onstart = null;
      recognition.onend = null;
      recognition.onerror = null;
      recognition.onresult = null;
      try { recognition.abort?.(); } catch { /* noop */ }
    }
    window.speechSynthesis?.cancel?.();
  }, []);

  const setHandsFree = React.useCallback((value) => {
    handsFreeRef.current = Boolean(value);
    setHandsFreeState(Boolean(value));
  }, []);

  const ensureMicrophoneAccess = React.useCallback(async ({ force = false } = {}) => {
    if (microphoneVerifiedRef.current && !force) return true;
    if (microphoneCheckPromiseRef.current) return microphoneCheckPromiseRef.current;

    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      // SpeechRecognition puede existir sin mediaDevices en algunos WebViews.
      // No lo marcamos como permiso denegado: dejamos que Recognition informe su error real.
      setMicrophoneState("unverified");
      return true;
    }

    setMicrophoneState("checking");
    setMicrophoneError("");
    const pending = (async () => {
      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        const tracks = stream?.getAudioTracks?.() || [];
        if (!tracks.length || tracks.every((track) => track.readyState === "ended")) {
          const error = new Error("No live audio track");
          error.name = "NotReadableError";
          throw error;
        }
        microphoneVerifiedRef.current = true;
        setMicrophoneState("ready");
        setMicrophoneError("");
        setNeedsMicPermission(false);
        return true;
      } catch (error) {
        microphoneVerifiedRef.current = false;
        const detail = microphoneErrorMessage(error);
        setMicrophoneState("error");
        setMicrophoneError(detail);
        setStatus("error");
        setIsListening(false);
        setNeedsMicPermission(["NotAllowedError", "SecurityError"].includes(String(error?.name || "")));
        setMessage(detail);
        return false;
      } finally {
        stopMediaStream(stream);
        microphoneCheckPromiseRef.current = null;
      }
    })();

    microphoneCheckPromiseRef.current = pending;
    return pending;
  }, []);

  const currentConfig = React.useCallback(() => formGetterRef.current?.() || null, []);

  const disposeRecognition = React.useCallback(({ abort = true, intentional = true } = {}) => {
    recognitionTokenRef.current += 1;
    pendingTranscriptRef.current = "";
    intentionalRecognitionStopRef.current = intentional;
    if (pendingListenTimerRef.current) {
      window.clearTimeout(pendingListenTimerRef.current);
      pendingListenTimerRef.current = null;
    }
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    setIsListening(false);
    setInterimHeard("");
    if (!recognition) return;
    recognition.onstart = null;
    recognition.onend = null;
    recognition.onerror = null;
    recognition.onresult = null;
    try {
      if (abort) recognition.abort?.();
      else recognition.stop?.();
    } catch { /* noop */ }
  }, []);

  const stopListening = React.useCallback(() => {
    disposeRecognition({ abort: false, intentional: true });
    if (!isSpeakingRef.current) setStatus("idle");
  }, [disposeRecognition]);

  const cancelSpeechForBargeIn = React.useCallback(() => {
    if (!isSpeakingRef.current) return;
    speechTokenRef.current += 1;
    currentSpeechTextRef.current = "";
    isSpeakingRef.current = false;
    setIsSpeaking(false);
    window.speechSynthesis?.cancel?.();
    setStatus(recognitionRef.current ? "listening" : "idle");
  }, []);

  const enqueueTranscript = React.useCallback((transcript) => {
    const clean = String(transcript || "").trim();
    if (!clean) return;

    // Chrome/WebKit puede entregar dos finales iguales en una misma respuesta.
    // Si el primer final ya avanzó el wizard, procesar el segundo contra el campo
    // siguiente hace que el formulario parezca retroceder o saltear pasos.
    const normalized = normalizeVoiceText(clean);
    const now = Date.now();
    const previous = lastQueuedVoiceFinalRef.current;
    if (normalized && previous.text === normalized && now - previous.at < 1800) return;
    lastQueuedVoiceFinalRef.current = { text: normalized, at: now };

    utteranceQueueRef.current = utteranceQueueRef.current
      .catch(() => undefined)
      .then(() => processRef.current?.(clean, { inputSource: "voice" }));
  }, []);

  const startListening = React.useCallback(({ oneShot = false } = {}) => {
    if (!speechSupported || !mountedRef.current) {
      setMessage("Este navegador no ofrece dictado. Podés escribir el comando en el mismo operador.");
      setHandsFree(false);
      return;
    }
    if (recognitionRef.current) return;

    intentionalRecognitionStopRef.current = false;
    recognitionModeRef.current = oneShot ? "oneshot" : "handsfree";
    const sessionToken = recognitionTokenRef.current + 1;
    recognitionTokenRef.current = sessionToken;

    try {
      const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new Recognition();
      recognition.lang = "es-AR";
      recognition.continuous = !oneShot;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      pendingTranscriptRef.current = "";

      const isCurrent = () => mountedRef.current
        && recognitionTokenRef.current === sessionToken
        && recognitionRef.current === recognition;

      recognition.onstart = () => {
        if (!isCurrent()) return;
        microphoneVerifiedRef.current = true;
        setMicrophoneState("ready");
        setMicrophoneError("");
        setNeedsMicPermission(false);
        setIsListening(true);
        if (!isSpeakingRef.current) setStatus("listening");
      };

      recognition.onresult = (event) => {
        if (!isCurrent()) return;
        let interim = "";
        const finals = [];
        for (let index = event.resultIndex || 0; index < (event.results?.length || 0); index += 1) {
          const row = event.results[index];
          const transcript = row?.[0]?.transcript?.trim?.() || "";
          if (!transcript) continue;
          if (row.isFinal) finals.push(transcript);
          else interim += `${interim ? " " : ""}${transcript}`;
        }

        if (interim) {
          setInterimHeard(interim);
          const interimWords = normalizeVoiceText(interim).split(" ").filter(Boolean);
          if (isSpeakingRef.current && interimWords.length >= 2 && !looksLikePromptEcho(interim, currentSpeechTextRef.current)) {
            cancelSpeechForBargeIn();
            setMessage(`Te escucho · ${interim}`);
          }
        }

        if (finals.length) {
          const transcript = finals.join(" ").trim();
          if (!transcript) return;
          if (isSpeakingRef.current && looksLikePromptEcho(transcript, currentSpeechTextRef.current)) return;
          if (isSpeakingRef.current) cancelSpeechForBargeIn();
          setInterimHeard("");
          setLastHeard(transcript);
          setStatus("processing");
          enqueueTranscript(transcript);
          if (oneShot) {
            intentionalRecognitionStopRef.current = true;
            try { recognition.stop?.(); } catch { /* noop */ }
          } else if (!isSpeakingRef.current) {
            setStatus("listening");
          }
        }
      };

      recognition.onend = () => {
        if (!isCurrent()) return;
        recognitionRef.current = null;
        setIsListening(false);
        setInterimHeard("");
        if (!mountedRef.current) return;
        const shouldRestart = handsFreeRef.current
          && recognitionModeRef.current === "handsfree"
          && !intentionalRecognitionStopRef.current;
        if (shouldRestart) {
          window.setTimeout(() => {
            if (mountedRef.current && handsFreeRef.current && !recognitionRef.current) startListening({ oneShot: false });
          }, 110);
        } else if (!isSpeakingRef.current) {
          setStatus("idle");
        }
      };

      recognition.onerror = (event) => {
        if (!isCurrent()) return;
        const error = event?.error || "";
        recognitionRef.current = null;
        setIsListening(false);
        setInterimHeard("");
        if (!mountedRef.current) return;
        if (error === "aborted") return;
        if (error === "no-speech") {
          if (handsFreeRef.current && !intentionalRecognitionStopRef.current) {
            window.setTimeout(() => startListening({ oneShot: false }), 180);
          }
          return;
        }

        const detail = recognitionErrorMessage(error, microphoneVerifiedRef.current);
        setStatus("error");
        setMicrophoneError(detail);

        if (error === "audio-capture") {
          microphoneVerifiedRef.current = false;
          setMicrophoneState("error");
          setHandsFree(false);
          setNeedsMicPermission(false);
        } else if (error === "not-allowed") {
          setHandsFree(false);
          setNeedsMicPermission(!microphoneVerifiedRef.current);
        } else if (error === "service-not-allowed" || error === "network" || error === "language-not-supported") {
          // Reintentar en loop estos errores puede crear una tormenta de Recognition.start().
          setHandsFree(false);
          setNeedsMicPermission(false);
        }

        setMessage(detail);
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (error) {
      recognitionRef.current = null;
      setIsListening(false);
      setStatus("error");
      const detail = `No pude iniciar SpeechRecognition${error?.name ? ` (${error.name})` : ""}.`;
      setMicrophoneError(detail);
      setNeedsMicPermission(false);
      setMessage(`${detail} Probá Reintentar micrófono.`);
    }
  }, [cancelSpeechForBargeIn, enqueueTranscript, setHandsFree, speechSupported]);

  React.useEffect(() => {
    const previous = previousRouteContextRef.current;
    previousRouteContextRef.current = routeContext;
    if (!previous || previous === routeContext) return undefined;

    // Cambiar de pantalla no debe dejar una instancia vieja de SpeechRecognition
    // colgada. Reciclamos sólo el transporte de voz: la conversación de negocio
    // sigue viva en el runtime global salvo que el usuario haya pedido Inicio.
    pendingNavigationChoiceRef.current = null;
    lastAdaptiveExecutionRef.current = null;
    pendingTranscriptRef.current = "";
    setInterimHeard("");
    if (!alwaysOnEnabledRef.current || !speechSupported) return undefined;

    disposeRecognition({ abort: true, intentional: true });
    setHandsFree(true);
    const timer = window.setTimeout(() => {
      if (!mountedRef.current || !alwaysOnEnabledRef.current || document.visibilityState === "hidden") return;
      if (!recognitionRef.current && !isSpeakingRef.current) startListening({ oneShot: false });
    }, 140);
    return () => window.clearTimeout(timer);
  }, [disposeRecognition, routeContext, setHandsFree, speechSupported, startListening]);

  const scheduleListening = React.useCallback((delay = 40) => {
    if (pendingListenTimerRef.current) window.clearTimeout(pendingListenTimerRef.current);
    pendingListenTimerRef.current = window.setTimeout(() => {
      pendingListenTimerRef.current = null;
      if (!recognitionRef.current) startListening({ oneShot: !handsFreeRef.current });
    }, delay);
  }, [startListening]);


  const speak = React.useCallback((text, { listenAfter = false, force = false } = {}) => {
    const clean = String(text || "").trim();
    if (!clean) return;
    setMessage(clean);

    // En manos libres, el micrófono se abre ANTES de hablar y permanece activo.
    // Esto habilita barge-in real: el usuario puede contestar encima de la voz.
    if (handsFreeRef.current && speechSupported && !recognitionRef.current) {
      startListening({ oneShot: false });
    }

    if (!speakSupported || (!autoRead && !force)) {
      if (listenAfter && handsFreeRef.current && !recognitionRef.current) scheduleListening(20);
      return;
    }

    const token = speechTokenRef.current + 1;
    speechTokenRef.current = token;
    currentSpeechTextRef.current = clean;
    speechStartedAtRef.current = Date.now();
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(clean);
    utterance.lang = "es-AR";
    utterance.rate = speechRate;
    utterance.pitch = 1;
    utterance.onstart = () => {
      if (!mountedRef.current || speechTokenRef.current !== token) return;
      isSpeakingRef.current = true;
      setIsSpeaking(true);
      setStatus("speaking");
    };
    const finishSpeech = () => {
      if (!mountedRef.current || speechTokenRef.current !== token) return;
      isSpeakingRef.current = false;
      setIsSpeaking(false);
      currentSpeechTextRef.current = "";
      setStatus(recognitionRef.current ? "listening" : "idle");
      if ((listenAfter || handsFreeRef.current) && handsFreeRef.current && !recognitionRef.current) scheduleListening(20);
    };
    utterance.onend = finishSpeech;
    utterance.onerror = finishSpeech;
    window.speechSynthesis.speak(utterance);
  }, [autoRead, scheduleListening, speakSupported, speechRate, speechSupported, startListening]);

  const interruptAndListen = React.useCallback(({ handsFree: keepHandsFree = true } = {}) => {
    setHandsFree(keepHandsFree);
    cancelSpeechForBargeIn();
    setMessage("Te escucho.");
    if (!recognitionRef.current) startListening({ oneShot: !keepHandsFree });
  }, [cancelSpeechForBargeIn, setHandsFree, startListening]);

  const stopConversation = React.useCallback(() => {
    setHandsFree(false);
    cancelSpeechForBargeIn();
    disposeRecognition({ abort: true, intentional: true });
    queuedActionAfterSaveRef.current = "";
    setStatus("idle");
    setMessage("Conversación pausada. Cuando quieras, seguimos.");
  }, [cancelSpeechForBargeIn, disposeRecognition, setHandsFree]);


  const askField = React.useCallback((fieldName, { forceListen = false } = {}) => {
    const config = currentConfig();
    if (!config) return;
    const fields = visibleFields(config);
    const field = fields.find((row) => row.name === fieldName) || fields[0];
    if (!field) {
      const missing = requiredMissing(config);
      if (missing.length) {
        setCurrentFieldName(missing[0].name);
        currentFieldRef.current = missing[0].name;
        focusVoiceField(missing[0].name);
        speak(`Me falta ${missing[0].label || missing[0].name}. ${missing[0].prompt || "Decime ese dato."}${formatOptions(missing[0])}`, { listenAfter: forceListen || handsFreeRef.current, force: forceListen });
        return;
      }
      setConfirmation(true);
      speak(`¿Guardo ${config.title}?`, { listenAfter: forceListen || handsFreeRef.current, force: forceListen });
      return;
    }
    setConfirmation(false);
    setCurrentFieldName(field.name);
    currentFieldRef.current = field.name;
    focusVoiceField(field.name);
    const value = config.values?.[field.name];
    const current = hasValue(value) && field.announceCurrent ? ` Ahora figura ${fieldValueLabel(field, value)}.` : "";
    speak(`${field.prompt || `Decime ${field.label || field.name}.`}${current}${formatOptions(field)}`, { listenAfter: forceListen || handsFreeRef.current, force: forceListen });
  }, [currentConfig, speak]);

  const nextField = React.useCallback((direction = 1, { forceListen = false } = {}) => {
    const config = currentConfig();
    if (!config) return;
    const fields = visibleFields(config);
    if (!fields.length) return askField("", { forceListen });
    const currentName = currentFieldRef.current;
    let index = fields.findIndex((row) => row.name === currentName);
    if (index < 0) index = direction > 0 ? -1 : fields.length;
    let nextIndex = index + direction;
    while (nextIndex >= 0 && nextIndex < fields.length) {
      const field = fields[nextIndex];
      if (!(field.skipIfFilled && hasValue(config.values?.[field.name]))) return askField(field.name, { forceListen });
      nextIndex += direction;
    }
    if (direction < 0) return askField(fields[0].name, { forceListen });
    const missing = requiredMissing(config);
    if (missing.length) return askField(missing[0].name, { forceListen });
    setConfirmation(true);
    setCurrentFieldName("");
    currentFieldRef.current = "";
    speak(`¿Guardo ${config.title}?`, { listenAfter: forceListen || handsFreeRef.current, force: forceListen });
  }, [askField, currentConfig, speak]);

  const beginCurrentForm = React.useCallback(({ handsFree: requestedHandsFree = true } = {}) => {
    const config = currentConfig();
    if (!config) {
      setHandsFree(requestedHandsFree);
      speak("¿Qué querés hacer?", { listenAfter: requestedHandsFree, force: true });
      return;
    }
    setHandsFree(requestedHandsFree);
    const fields = visibleFields(config);
    const first = fields.find((field) => !field.skipIfFilled || !hasValue(config.values?.[field.name])) || fields[0];
    if (!first) return;
    askField(first.name, { forceListen: requestedHandsFree });
  }, [askField, currentConfig, setHandsFree, speak]);

  const registerForm = React.useCallback((id, getter) => {
    activeFormIdRef.current = id;
    formGetterRef.current = getter;
    setActiveFormId(id);
    setConfirmation(false);
    const pending = pendingFormRef.current;
    if (pending && pending === id) {
      pendingFormRef.current = "";
      window.setTimeout(() => beginCurrentForm({ handsFree: handsFreeRef.current }), 35);
    }
    return () => {
      if (activeFormIdRef.current !== id) return;
      activeFormIdRef.current = "";
      formGetterRef.current = null;
      currentFieldRef.current = "";
      setActiveFormId("");
      setCurrentFieldName("");
      setConfirmation(false);
      document.querySelectorAll?.('[data-owner-voice-active="true"]').forEach((node) => node.removeAttribute("data-owner-voice-active"));
    };
  }, [beginCurrentForm]);

  const registerConversationHandler = React.useCallback((handler) => {
    conversationHandlerRef.current = typeof handler === "function" ? handler : null;
    return () => {
      if (conversationHandlerRef.current === handler) conversationHandlerRef.current = null;
    };
  }, []);

  const findActionKey = React.useCallback((text, { excludeFormId = "" } = {}) => {
    const normalized = normalizeVoiceText(text);
    for (const [key, action] of Object.entries(actions)) {
      if (excludeFormId && action.formId === excludeFormId) continue;
      const aliases = [action.label, ...(action.aliases || [])].filter(Boolean);
      if (aliases.some((alias) => normalized.includes(normalizeVoiceText(alias)))) return key;
    }
    return "";
  }, [actions]);

  const findDirectShortcutKey = React.useCallback((text) => {
    const normalized = normalizeVoiceText(text);
    const writeVerb = /\b(?:anot\w*|carg\w*|registr\w*|sum\w*|agreg\w*|quiero anotar|quiero cargar|quiero registrar)\b/.test(normalized);
    if (!writeVerb) return "";
    if (/\b(?:gasto|ingreso|movimiento|transferencia|compra|pago)\b/.test(normalized) && actions.finance?.run) return "finance";
    if (/\b(?:tarea|seguimiento|recordatorio|turno|pendiente)\b/.test(normalized) && actions.commitment?.run) return "commitment";
    if (/\b(?:propuesta|presupuesto|cotizacion)\b/.test(normalized) && actions.proposal?.run) return "proposal";
    return "";
  }, [actions]);

  const executeAction = React.useCallback((key, { handsFree: requestedHandsFree = false, context = continuationRef.current } = {}) => {
    const action = actions[key];
    if (!action?.run) return false;
    if (requestedHandsFree) setHandsFree(true);
    if (action.resetContext) {
      continuationRef.current = null;
      actionContextRef.current = null;
      context = null;
    } else if (context) {
      actionContextRef.current = context;
    }
    if (action.formId) pendingFormRef.current = action.formId;
    action.run(context);
    if (action.highlightSection) window.setTimeout(() => highlightOwnerSection(action.highlightSection), 80);
    const ack = typeof action.ack === "function" ? action.ack(context) : (action.ack || `Abro ${action.label || key}.`);
    if (requestedHandsFree && action.formId) setMessage(ack);
    else if (ack) speak(ack, { force: requestedHandsFree });
    return true;
  }, [actions, setHandsFree, speak]);

  const resetOwnerConversation = React.useCallback(async ({ navigateHome = false, announce = true } = {}) => {
    const config = currentConfig();
    try { await config?.onCancel?.(); } catch { /* reset nunca debe quedar bloqueado por un diálogo */ }

    activeFormIdRef.current = "";
    formGetterRef.current = null;
    currentFieldRef.current = "";
    pendingFormRef.current = "";
    continuationRef.current = null;
    actionContextRef.current = null;
    queuedActionAfterSaveRef.current = "";
    pendingNavigationChoiceRef.current = null;
    lastAdaptiveExecutionRef.current = null;
    pendingTranscriptRef.current = "";
    setActiveFormId("");
    setCurrentFieldName("");
    setConfirmation(false);
    setInterimHeard("");
    setStatus(recognitionRef.current ? "listening" : "idle");
    resetConversationState?.();

    try { window.sessionStorage.removeItem("wp-owner-jarvis-turn-v4"); } catch { /* sessionStorage opcional */ }
    try { window.dispatchEvent(new CustomEvent("owner:jarvis-conversation-reset", { detail: { navigateHome } })); } catch { /* noop */ }

    if (navigateHome) {
      const didNavigate = executeAction("today", { handsFree: handsFreeRef.current, context: null });
      if (!didNavigate && announce) speak("Volví a Inicio. La conversación anterior quedó cerrada.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      return true;
    }
    if (announce) speak("Reinicié la conversación. Decime qué querés resolver.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
    else setMessage("¿Qué querés hacer?");
    return true;
  }, [currentConfig, executeAction, resetConversationState, speak]);

  const adaptiveRoute = ownerRouteContext(routeContext || (typeof window !== "undefined" ? window.location.pathname : ""));

  const saveNavigationFeedback = React.useCallback(async ({ utterance, raw, intentKey, intentLabel, mode = "positive" }) => {
    if (typeof recordNavigationLearning !== "function" || !utterance || !intentKey) return;
    try {
      const response = await recordNavigationLearning({
        utterance,
        raw: raw || utterance,
        routeContext: adaptiveRoute,
        intentKey,
        intentLabel,
        mode,
      });
      const row = response?.learning;
      if (!row) return;
      setNavigationLearning((previous) => {
        const filtered = previous.filter((item) => String(item.id || "") !== String(row.id || "")
          && !(item.utteranceKey === row.utteranceKey && item.routeContext === row.routeContext && item.intentKey === row.intentKey));
        return [row, ...filtered].slice(0, 800);
      });
    } catch { /* aprendizaje nunca debe romper la navegación */ }
  }, [adaptiveRoute, recordNavigationLearning]);

  const adaptiveCandidates = React.useCallback((utterance, { excludeKeys = [] } = {}) => {
    return rankAdaptiveActions({
      utterance,
      actions,
      learning: navigationLearning,
      routeContext: adaptiveRoute,
      excludeKeys,
    });
  }, [actions, adaptiveRoute, navigationLearning]);

  const askAdaptiveChoice = React.useCallback((utterance, ranked, { excludeKeys = [] } = {}) => {
    const usable = (ranked?.length ? ranked : adaptiveCandidates(utterance, { excludeKeys })).slice(0, 3);
    if (!usable.length) return false;
    pendingNavigationChoiceRef.current = { utterance, candidates: usable, createdAt: Date.now() };
    const labels = usable.map((row) => row.label).filter(Boolean);
    const hint = labels.length > 1
      ? `No estoy seguro si querías ${labels.slice(0, 2).join(" o ")}.`
      : `No estoy seguro si querías ${labels[0]}.`;
    speak(`${hint} Decime qué querías hacer con tus palabras.`, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
    return true;
  }, [adaptiveCandidates, speak]);

  React.useEffect(() => {
    const previousRoute = previousRouteContextRef.current;
    previousRouteContextRef.current = routeContext;
    if (!previousRoute || previousRoute === routeContext) return;
    const pending = pendingNavigationChoiceRef.current;
    if (!pending || Date.now() - pending.createdAt > 20000) return;
    const cleanPath = String(routeContext || "").split("?")[0];
    const selected = pending.candidates.find((candidate) => {
      const target = String(actions[candidate.key]?.route || "").split("?")[0];
      return target && target === cleanPath;
    });
    if (!selected) return;
    pendingNavigationChoiceRef.current = null;
    saveNavigationFeedback({
      utterance: pending.utterance,
      raw: pending.utterance,
      intentKey: selected.key,
      intentLabel: selected.label,
      mode: "positive",
    });
    setMessage(`Entendido. Aprendí que “${pending.utterance}” en este contexto puede significar ${selected.label}.`);
  }, [actions, routeContext, saveNavigationFeedback]);

  const submitCurrentForm = React.useCallback(async ({ nextActionKey = "" } = {}) => {
    const config = currentConfig();
    if (!config) return false;
    const missing = requiredMissing(config);
    if (missing.length) {
      askField(missing[0].name, { forceListen: handsFreeRef.current });
      return false;
    }

    setStatus("saving");
    setMessage(`Guardando ${config.title}…`);
    const previousContext = actionContextRef.current || continuationRef.current;
    try {
      const result = await config.onSubmit?.();
      if (result === false) {
        setStatus("error");
        speak("No pude guardarlo. Revisá el formulario y corregimos lo que falte.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
        return false;
      }

      setConfirmation(false);
      // Liberamos el formulario en la capa de voz inmediatamente. La UI puede
      // cerrar unos milisegundos después, pero la próxima orden ya se interpreta
      // como acción global/contextual y no como dato del formulario anterior.
      activeFormIdRef.current = "";
      formGetterRef.current = null;
      currentFieldRef.current = "";
      setActiveFormId("");
      setCurrentFieldName("");
      document.querySelectorAll?.('[data-owner-voice-active="true"]').forEach((node) => node.removeAttribute("data-owner-voice-active"));
      let continuation = null;
      if (typeof config.afterSubmit === "function") continuation = config.afterSubmit(result, previousContext) || null;
      else if (config.afterSubmit) continuation = config.afterSubmit;

      if (continuation?.context !== undefined) {
        continuationRef.current = continuation.context;
        actionContextRef.current = continuation.context;
      } else if (previousContext) {
        continuationRef.current = previousContext;
        actionContextRef.current = previousContext;
      }

      const context = continuationRef.current;
      setStatus(recognitionRef.current ? "listening" : "idle");

      if (nextActionKey) {
        queuedActionAfterSaveRef.current = "";
        window.setTimeout(() => executeAction(nextActionKey, { handsFree: handsFreeRef.current, context }), 45);
        return true;
      }

      const fallbackPrompt = context
        ? `Listo. ¿Qué querés hacer ahora con ${contextLabel(context)}?`
        : `${config.successMessage || "Listo."} ¿Qué querés hacer ahora?`;
      const prompt = continuation?.prompt || fallbackPrompt;
      if (handsFreeRef.current) speak(prompt, { listenAfter: true, force: true });
      else setMessage(prompt);
      return true;
    } catch {
      setStatus("error");
      speak("No pude guardarlo. Revisá el formulario y probamos otra vez.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      return false;
    }
  }, [askField, currentConfig, executeAction, speak]);

  const processUtterance = React.useCallback(async (raw, meta = {}) => {
    const text = normalizeVoiceText(raw);
    if (!text) return;
    if (matchesCommand(text, COMMANDS.powerOff)) {
      setAlwaysOnEnabledState(false);
      alwaysOnEnabledRef.current = false;
      try { window.localStorage.setItem("wp-owner-jarvis-always-on", "0"); } catch { /* noop */ }
      setHandsFree(false);
      setMessage("Jarvis apagado. Tocá el micrófono o Encender para volver a activarlo.");
      speak("Listo. Me apago.", { listenAfter: false, force: true });
      window.setTimeout(() => stopConversation(), 850);
      return;
    }
    if (matchesCommand(text, COMMANDS.powerOn)) {
      setAlwaysOnEnabledState(true);
      alwaysOnEnabledRef.current = true;
      setNeedsMicPermission(false);
      try { window.localStorage.setItem("wp-owner-jarvis-always-on", "1"); } catch { /* noop */ }
      setHandsFree(true);
      speak("Listo. Estoy encendido. ¿Qué necesitás?", { listenAfter: true, force: true });
      return;
    }
    if (matchesCommand(text, COMMANDS.stop)) {
      stopConversation();
      return;
    }
    if (isOwnerHomeResetIntent(text)) {
      await resetOwnerConversation({ navigateHome: true, announce: true });
      return;
    }

    const pendingChoice = pendingNavigationChoiceRef.current;
    if (pendingChoice && !currentConfig()) {
      if (isNegative(text) || matchesCommand(text, COMMANDS.cancel) || /\b(?:ninguna|ninguno|otra cosa|ninguna sirve)\b/.test(text)) {
        pendingNavigationChoiceRef.current = null;
        speak("Entendido. No voy a adivinar. Decime qué querías hacer con otras palabras y vuelvo a calcularlo.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
        return;
      }
      const byLabel = pendingChoice.candidates
        .map((row) => ({ row, score: rankAdaptiveActions({ utterance: raw, actions: { [row.key]: actions[row.key] }, learning: [], routeContext: adaptiveRoute })[0]?.score || 0 }))
        .sort((a, b) => b.score - a.score)[0];
      const selected = byLabel?.score >= 0.72 ? byLabel.row : null;
      if (selected) {
        pendingNavigationChoiceRef.current = null;
        await saveNavigationFeedback({ utterance: pendingChoice.utterance, raw: pendingChoice.utterance, intentKey: selected.key, intentLabel: selected.label, mode: "positive" });
        lastAdaptiveExecutionRef.current = { utterance: pendingChoice.utterance, candidate: selected, executedAt: Date.now(), learnedFromChoice: true };
        executeAction(selected.key, { handsFree: handsFreeRef.current, context: continuationRef.current });
        return;
      }
      // No encerramos al usuario en una selección artificial. Si no coincide con
      // las sugerencias anteriores, soltamos esa ambigüedad y procesamos la frase
      // normalmente para que Jarvis pueda entender una intención nueva.
      pendingNavigationChoiceRef.current = null;
    }

    const lastAdaptive = lastAdaptiveExecutionRef.current;
    if (!currentConfig() && lastAdaptive && Date.now() - lastAdaptive.executedAt < 15000
      && /\b(?:no era eso|no es eso|te equivocaste|equivocado|otra opcion|otra opción|mal jarvis)\b/.test(text)) {
      await saveNavigationFeedback({ utterance: lastAdaptive.utterance, raw: lastAdaptive.utterance, intentKey: lastAdaptive.candidate.key, intentLabel: lastAdaptive.candidate.label, mode: "negative" });
      lastAdaptiveExecutionRef.current = null;
      const alternatives = adaptiveCandidates(lastAdaptive.utterance, { excludeKeys: [lastAdaptive.candidate.key] });
      if (!askAdaptiveChoice(lastAdaptive.utterance, alternatives, { excludeKeys: [lastAdaptive.candidate.key] })) {
        speak("Entendido. Corregí esa asociación. Decime qué querías hacer.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      }
      return;
    }

    if (matchesCommand(text, COMMANDS.repeat)) {
      const repeatConfig = currentConfig();
      if (repeatConfig && currentFieldRef.current) {
        const repeatField = visibleFields(repeatConfig).find((row) => row.name === currentFieldRef.current);
        if (repeatField) {
          speak(`${repeatField.prompt || repeatField.label}.${formatOptions(repeatField)}`, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
          return;
        }
      }
      speak(message || "¿Qué querés hacer?", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      return;
    }

    const config = currentConfig();
    if (!config) {
      if (matchesCommand(text, COMMANDS.options)) {
        const continuation = continuationRef.current;
        if (continuation?.prompt) speak(continuation.prompt, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
        else speak("Podés preguntarme qué tenés hoy, qué hacer ahora, cómo viene tu plata, qué hiciste entre dos fechas o cuánto trabajaste. También podés decir abrí la ficha de un cliente, proyecto, rutina o día; crear una tarea, rutina, propuesta o movimiento; registrar lo que hiciste; o anotar que seguido de algo que quieras guardar.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
        return;
      }
      // Escrituras obvias no necesitan entrar al árbol conversacional ni esperar al backend.
      // Esto hace que "quiero anotar un gasto" abra el formulario de inmediato desde cualquier sección.
      const directShortcutKey = findDirectShortcutKey(text);
      if (directShortcutKey) {
        executeAction(directShortcutKey, { handsFree: handsFreeRef.current, context: continuationRef.current });
        return;
      }
      // Si el runtime clásico tiene una conversación pendiente (por ejemplo un lote
      // financiero esperando proyecto/contrato o una confirmación sensible), esa
      // continuidad tiene prioridad global aunque la navegación haya montado otro
      // OwnerJarvis2. El estado vive en el runtime del workspace, no en la pantalla.
      let priorityUnhandledTried = false;
      if (typeof onUnhandledUtterance === "function" && hasPriorityConversation?.()) {
        priorityUnhandledTried = true;
        try {
          const result = await onUnhandledUtterance({ raw, text, inputSource: meta.inputSource || "voice" });
          if (result?.handled) {
            if (result.highlightSection) highlightOwnerSection(result.highlightSection);
            if (result.message) speak(result.message, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
            return;
          }
        } catch {
          speak("No pude continuar la operación que teníamos abierta. No hice cambios; probá de nuevo.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
          return;
        }
      }

      if (typeof conversationHandlerRef.current === "function") {
        try {
          const conversationResult = await conversationHandlerRef.current({ raw, text, inputSource: meta.inputSource || "voice" });
          if (conversationResult?.handled) {
            if (conversationResult.highlightSection) highlightOwnerSection(conversationResult.highlightSection);
            if (conversationResult.message) speak(conversationResult.message, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
            return;
          }
        } catch {
          // La conversación avanzada es opcional: si falla, los comandos clásicos siguen disponibles.
        }
      }
      // Las consultas/navegación contextual tienen prioridad sobre aliases genéricos
      // como "proyecto" o "rutina". Así "abrí proyecto WebProfit" no abre
      // accidentalmente el formulario de crear proyecto.
      if (!priorityUnhandledTried && typeof onUnhandledUtterance === "function") {
        try {
          const result = await onUnhandledUtterance({ raw, text, inputSource: meta.inputSource || "voice" });
          if (result?.handled) {
            if (result.highlightSection) highlightOwnerSection(result.highlightSection);
            if (result.message) speak(result.message, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
            return;
          }
        } catch {
          speak("No pude consultar tus datos ahora. Probá de nuevo o pedime una acción concreta.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
          return;
        }
      }
      const actionKey = findActionKey(text);
      if (actionKey) {
        executeAction(actionKey, { handsFree: handsFreeRef.current, context: continuationRef.current });
        return;
      }

      const ranked = adaptiveCandidates(raw);
      const adaptive = adaptiveDecision(ranked);
      if (adaptive.mode === "auto" && adaptive.candidate) {
        lastAdaptiveExecutionRef.current = { utterance: raw, candidate: adaptive.candidate, executedAt: Date.now(), learnedFromChoice: false };
        executeAction(adaptive.candidate.key, { handsFree: handsFreeRef.current, context: continuationRef.current });
        return;
      }
      if (adaptive.mode === "ask" && askAdaptiveChoice(raw, adaptive.candidates)) return;

      const continuation = continuationRef.current;
      if (continuation?.prompt) {
        speak(continuation.prompt, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      } else {
        speak("No entendí esa orden y no tengo suficiente confianza para adivinar. Decímela de otra forma.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      }
      return;
    }

    const globalShortcutKey = findDirectShortcutKey(text);
    if (globalShortcutKey || looksLikeGlobalEscapeIntent(text)) {
      // Una orden global explícita corta el formulario actual. No obligamos al usuario
      // a decir "cancelar" antes de navegar o abrir otra captura.
      await config.onCancel?.();
      activeFormIdRef.current = "";
      formGetterRef.current = null;
      currentFieldRef.current = "";
      setActiveFormId("");
      setCurrentFieldName("");
      setConfirmation(false);
      if (globalShortcutKey) {
        executeAction(globalShortcutKey, { handsFree: handsFreeRef.current, context: continuationRef.current });
        return;
      }
      if (typeof onUnhandledUtterance === "function") {
        try {
          const result = await onUnhandledUtterance({ raw, text, inputSource: meta.inputSource || "voice" });
          if (result?.handled) {
            if (result.highlightSection) highlightOwnerSection(result.highlightSection);
            if (result.message) speak(result.message, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
            return;
          }
        } catch {
          speak("No pude resolver esa navegación ahora. El formulario quedó cerrado para que puedas reintentar.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
          return;
        }
      }
      speak("Cerré el formulario anterior, pero no pude resolver esa orden. Decímela de otra forma.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      return;
    }

    if (matchesCommand(text, COMMANDS.cancel)) {
      await config.onCancel?.();
      activeFormIdRef.current = "";
      formGetterRef.current = null;
      currentFieldRef.current = "";
      setActiveFormId("");
      setCurrentFieldName("");
      const continuation = continuationRef.current;
      const prompt = continuation?.prompt || `${config.title} cancelado. ¿Qué querés hacer ahora?`;
      speak(prompt, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      return;
    }
    if (matchesCommand(text, COMMANDS.options)) {
      speak("En este formulario podés responder el campo actual, decir repetir, siguiente, anterior, qué falta, guardar o cancelar.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      return;
    }
    if (matchesCommand(text, COMMANDS.missing)) {
      const missing = requiredMissing(config);
      if (!missing.length) speak("No falta ningún campo obligatorio. Podés decir guardar.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      else speak(`Falta ${missing.map((field) => field.label || field.name).join(", ")}.`, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      return;
    }
    if (matchesCommand(text, COMMANDS.previous)) return nextField(-1, { forceListen: handsFreeRef.current });
    if (matchesCommand(text, COMMANDS.next)) {
      const current = visibleFields(config).find((field) => field.name === currentFieldRef.current);
      const required = current && (typeof current.required === "function" ? current.required(config.values || {}) : current.required);
      if (required && !hasValue(config.values?.[current.name])) {
        speak(`${current.label || current.name} es obligatorio. Decime ese dato antes de seguir.`, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
        return;
      }
      return nextField(1, { forceListen: handsFreeRef.current });
    }
    if (matchesCommand(text, COMMANDS.startForm)) return beginCurrentForm({ handsFree: true });

    const chainedAction = findActionKey(text, { excludeFormId: activeFormIdRef.current });
    if (confirmation) {
      if (isAffirmative(text)) {
        await submitCurrentForm({ nextActionKey: chainedAction });
        return;
      }
      if (isNegative(text)) {
        setConfirmation(false);
        speak("Decime qué campo querés corregir.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
        return;
      }
    }

    if (matchesCommand(text, COMMANDS.save)) {
      // "Guardar" ya es una orden explícita: no obligamos a confirmar dos veces.
      await submitCurrentForm({ nextActionKey: chainedAction });
      return;
    }

    const fields = visibleFields(config);
    const explicitField = findFieldByVoice(text, fields);
    let field = explicitField || fields.find((row) => row.name === currentFieldRef.current) || fields[0];
    if (!field) return;

    if (explicitField && explicitField.name !== currentFieldRef.current) {
      setCurrentFieldName(explicitField.name);
      currentFieldRef.current = explicitField.name;
      focusVoiceField(explicitField.name);
      const commandValue = cleanFieldCommand(raw, explicitField);
      if (normalizeVoiceText(commandValue) === text || !commandValue.trim()) {
        speak(`${explicitField.prompt || `Decime ${explicitField.label}.`}${formatOptions(explicitField)}`, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
        return;
      }
      field = explicitField;
    }

    const parsed = parseFieldValue(field, raw, config);
    if (parsed === null || parsed === undefined || (typeof parsed === "number" && !Number.isFinite(parsed))) {
      speak(`No pude interpretar ${field.label || field.name}. ${field.prompt || "Decímelo de otra forma."}${formatOptions(field)}`, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      return;
    }
    config.setValue?.(field.name, parsed);
    setMessage(`${field.label || field.name}: ${fieldValueLabel(field, parsed)}.`);
    window.setTimeout(() => nextField(1, { forceListen: handsFreeRef.current }), 20);
  }, [adaptiveCandidates, adaptiveRoute, askAdaptiveChoice, askField, beginCurrentForm, confirmation, currentConfig, executeAction, findActionKey, findDirectShortcutKey, hasPriorityConversation, message, nextField, onUnhandledUtterance, resetOwnerConversation, saveNavigationFeedback, setHandsFree, speak, stopConversation, submitCurrentForm]);

  processRef.current = processUtterance;

  const sendText = React.useCallback((value) => {
    const clean = String(value || "").trim();
    if (!clean) return;
    setLastHeard(clean);
    processRef.current?.(clean, { inputSource: "text" });
  }, []);

  const enableAlwaysOn = React.useCallback(async ({ greet = true } = {}) => {
    setAlwaysOnEnabledState(true);
    alwaysOnEnabledRef.current = true;
    try { window.localStorage.setItem("wp-owner-jarvis-always-on", "1"); } catch { /* noop */ }
    setHandsFree(true);

    // Esta llamada ocurre desde el click del usuario. Primero comprobamos que el
    // audio físico abre de verdad; permiso "Permitido" no garantiza captura.
    const micReady = await ensureMicrophoneAccess({ force: microphoneState === "error" });
    if (!micReady) {
      setHandsFree(false);
      return false;
    }

    setNeedsMicPermission(false);
    setMicrophoneError("");
    if (!recognitionRef.current) startListening({ oneShot: false });
    if (greet) speak(introMessage || "Jarvis activo. Te escucho.", { listenAfter: true, force: true });
    else setMessage("Jarvis activo en Owner 360. Te escucho y mantengo el contexto mientras navegás.");
    return true;
  }, [ensureMicrophoneAccess, introMessage, microphoneState, setHandsFree, speak, startListening]);

  const disableAlwaysOn = React.useCallback(() => {
    setAlwaysOnEnabledState(false);
    alwaysOnEnabledRef.current = false;
    try { window.localStorage.setItem("wp-owner-jarvis-always-on", "0"); } catch { /* noop */ }
    stopConversation();
    setMessage("Jarvis quedó dormido. Podés reactivarlo desde esta barra.");
  }, [stopConversation]);

  React.useEffect(() => {
    if (!alwaysOn || !alwaysOnEnabled || !speechSupported) return undefined;
    const reengage = () => {
      if (!mountedRef.current || !alwaysOnEnabledRef.current || needsMicPermission || document.visibilityState === "hidden") return;
      setHandsFree(true);
      if (!recognitionRef.current && !isSpeakingRef.current) startListening({ oneShot: false });
    };
    const timer = window.setTimeout(() => {
      setMessage("Jarvis activo en Owner 360. Te escucho y mantengo el contexto mientras navegás.");
      reengage();
    }, 350);
    const onVisibility = () => { if (document.visibilityState === "visible") reengage(); };
    window.addEventListener("focus", reengage);
    window.addEventListener("online", reengage);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", reengage);
      window.removeEventListener("online", reengage);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [alwaysOn, alwaysOnEnabled, needsMicPermission, setHandsFree, speechSupported, startListening]);

  const startGlobalConversation = React.useCallback(() => {
    if (alwaysOn) {
      enableAlwaysOn({ greet: true });
      return;
    }
    setHandsFree(true);
    if (!recognitionRef.current) startListening({ oneShot: false });
    speak(introMessage || "¿Qué querés hacer?", { listenAfter: true, force: true });
  }, [alwaysOn, enableAlwaysOn, introMessage, setHandsFree, speak, startListening]);

  const toggleOneShotMic = React.useCallback(async () => {
    if (handsFreeRef.current) {
      stopConversation();
      return;
    }
    if (isListening) {
      stopListening();
      return;
    }
    if (isSpeaking) cancelSpeechForBargeIn();
    setHandsFree(false);
    if (activeFormIdRef.current && !currentFieldRef.current) beginCurrentForm({ handsFree: false });
    const micReady = await ensureMicrophoneAccess({ force: microphoneState === "error" });
    if (!micReady) return;
    startListening({ oneShot: true });
  }, [beginCurrentForm, cancelSpeechForBargeIn, ensureMicrophoneAccess, isListening, isSpeaking, microphoneState, setHandsFree, startListening, stopConversation, stopListening]);

  const say = React.useCallback((text, { listenAfter = false, force = true } = {}) => {
    if (listenAfter) setHandsFree(true);
    if (text) speak(text, { listenAfter, force });
  }, [setHandsFree, speak]);

  const guideToSection = React.useCallback((sectionId, text, { listenAfter = false, force = true, scroll = true } = {}) => {
    highlightOwnerSection(sectionId, { scroll });
    say(text, { listenAfter, force });
  }, [say]);

  const readCurrent = React.useCallback(() => {
    const config = currentConfig();
    if (config && currentFieldRef.current) {
      const field = visibleFields(config).find((row) => row.name === currentFieldRef.current);
      if (field) return speak(`${field.prompt || field.label}.${formatOptions(field)}`, { force: true });
    }
    speak(message || "¿Qué querés hacer?", { force: true });
  }, [currentConfig, message, speak]);

  const changeAutoRead = React.useCallback((value) => {
    setAutoRead(Boolean(value));
    try { window.localStorage.setItem("wp-owner-voice-auto-read", value ? "1" : "0"); } catch { /* noop */ }
  }, []);

  const changeSpeechRate = React.useCallback((value) => {
    const next = Number(value);
    const safe = [1, 1.15, 1.3, 1.5, 1.7].includes(next) ? next : 1.5;
    setSpeechRate(safe);
    try { window.localStorage.setItem("wp-owner-voice-rate", String(safe)); } catch { /* noop */ }
  }, []);

  const priorityConversationActive = React.useCallback(() => Boolean(hasPriorityConversation?.()), [hasPriorityConversation]);

  const value = React.useMemo(() => ({
    activeFormId,
    alwaysOnEnabled,
    autoRead,
    beginCurrentForm,
    changeAutoRead,
    changeSpeechRate,
    confirmation,
    currentConfig,
    currentFieldName,
    disableAlwaysOn,
    enableAlwaysOn,
    executeAction,
    handsFree,
    guideToSection,
    interimHeard,
    interruptAndListen,
    isListening,
    isSpeaking,
    lastHeard,
    message,
    microphoneError,
    microphoneState,
    needsMicPermission,
    priorityConversationActive,
    resetOwnerConversation,
    readCurrent,
    registerConversationHandler,
    registerForm,
    say,
    sendText,
    speakSupported,
    speechRate,
    speechSupported,
    startGlobalConversation,
    status,
    stopConversation,
    submitCurrentForm,
    toggleOneShotMic,
  }), [activeFormId, alwaysOnEnabled, autoRead, beginCurrentForm, changeAutoRead, changeSpeechRate, confirmation, currentConfig, currentFieldName, disableAlwaysOn, enableAlwaysOn, executeAction, handsFree, guideToSection, interimHeard, interruptAndListen, isListening, isSpeaking, lastHeard, message, microphoneError, microphoneState, needsMicPermission, priorityConversationActive, readCurrent, registerConversationHandler, registerForm, resetOwnerConversation, say, sendText, speakSupported, speechRate, speechSupported, startGlobalConversation, status, stopConversation, submitCurrentForm, toggleOneShotMic]);

  return (
    <OwnerVoiceContext.Provider value={value}>
      <GlobalStyles styles={{
        '[data-owner-voice-active="true"]': {
          outline: "3px solid rgba(243,90,58,.24)",
          outlineOffset: "3px",
          borderRadius: "10px",
          transition: "outline-color .18s ease",
        },
        '[data-owner-jarvis-focus="true"]': {
          outline: "3px solid rgba(243,90,58,.42)",
          outlineOffset: "4px",
          boxShadow: "0 0 0 8px rgba(243,90,58,.08), 0 18px 50px rgba(20,31,42,.12)",
          transform: "translateY(-1px)",
          transition: "outline-color .2s ease, box-shadow .2s ease, transform .2s ease",
        },
      }} />
      {children}
    </OwnerVoiceContext.Provider>
  );
}

export function useOwnerVoiceOperator() {
  return React.useContext(OwnerVoiceContext);
}

export function useOwnerVoiceForm({ id, open, config }) {
  const voice = useOwnerVoiceOperator();
  const configRef = React.useRef(config);
  configRef.current = config;
  React.useEffect(() => {
    if (!open || !voice?.registerForm || !id) return undefined;
    return voice.registerForm(id, () => configRef.current);
  }, [id, open, voice?.registerForm]);
  return voice;
}

export function voiceFieldProps(name) {
  return { "data-owner-voice-field": name };
}

export function OwnerVoiceFormControls({ label = "Completar por voz" }) {
  const voice = useOwnerVoiceOperator();
  if (!voice) return null;
  const config = voice.currentConfig?.();
  const fieldLabel = config?.fields?.find((field) => field.name === voice.currentFieldName)?.label || voice.currentFieldName;
  return (
    <Box sx={{ mt: .8, p: .9, borderRadius: 2.2, bgcolor: "rgba(243,90,58,.055)", border: "1px solid rgba(243,90,58,.13)" }}>
      <Stack direction="row" spacing={.7} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
        <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" label={voice.handsFree ? "VOZ · CONTINUA" : "VOZ"} color={voice.handsFree ? "primary" : "default"} sx={{ height: 22, fontWeight: 1000 }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 760, color: "text.secondary" }}>
            {fieldLabel ? `Campo activo: ${fieldLabel}` : "La voz completa este mismo formulario."}
          </Typography>
          {voice.handsFree && voice.isListening ? <Typography sx={{ fontSize: 11.5, fontWeight: 850, color: "success.main" }}>Escuchando incluso mientras habla</Typography> : null}
        </Stack>
        <Stack direction="row" spacing={.5}>
          <Tooltip title="Leer indicación"><span><IconButton size="small" disabled={!voice.speakSupported} onClick={voice.readCurrent}><VolumeUpRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
          {voice.isSpeaking && voice.handsFree ? (
            <Button size="small" variant="text" onClick={() => voice.interruptAndListen({ handsFree: true })} sx={{ textTransform: "none", fontWeight: 900 }}>Silenciar bot</Button>
          ) : null}
          <Button
            size="small"
            variant={voice.handsFree ? "contained" : "outlined"}
            startIcon={voice.handsFree ? <StopCircleRoundedIcon /> : <MicRoundedIcon />}
            onClick={() => voice.handsFree ? voice.stopConversation() : voice.beginCurrentForm({ handsFree: true })}
            disabled={!voice.speechSupported}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            {voice.handsFree ? "Pausar conversación" : label}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
